import Component from '@glimmer/component'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingDetailedControls } from 'teacher-ui-client/components/reporting/detailed-controls'
import { ReportingNoDataMessageIf } from 'teacher-ui-client/components/reporting/no-data-message-if'
import { ReportingMapSelector } from 'teacher-ui-client/components/reporting/map-selector'
import RouteTemplate from 'ember-route-template'
import { CalendarIcon } from 'teacher-ui-client/components/calendar-icon'
import type ReStudentSummaryCourseProgressSpellingRoute from 'teacher-ui-client/reporting/ui-scope/re/student-summary/course-progress/spelling/route'
import type ReStudentSummaryCourseProgressSpellingController from 'teacher-ui-client/reporting/ui-scope/re/student-summary/course-progress/spelling/controller'
import { t } from 'ember-intl'
import { array } from '@ember/helper'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { not, and } from 'ember-truth-helpers'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import { mapValues, truncate, uniq } from 'lodash'
import { unixTimeToCalendarIconArgs } from 'teacher-ui-client/utils/unix-time-to-calendar-icon'
import transposeObjects from 'teacher-ui-client/utils/transpose-objects'
import type { IntlService } from 'ember-intl'
import { service } from '@ember/service'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import { isPresent } from '@ember/utils'

interface Signature {
  Args: {
    model: ModelFor<ReStudentSummaryCourseProgressSpellingRoute>
    controller: ReStudentSummaryCourseProgressSpellingController
  }
}

export class ReStudentSummaryCourseProgressSpellingPage extends Component<Signature> {
  @service intl!: IntlService
  @service lessonInfo!: LessonInfoService

  get completedLessonsData() {
    return this.args.model.spellingLessonDetails ?? []
  }

  get endOfMapData() {
    return this.args.model.spellingMapDetails ?? []
  }

  get currentMapName() {
    const firstLessonOfMap = this.lessonInfo
      .lessonsFor(Precinct.RE_SPELLING)
      .find((lesson) => lesson.map === this.args.controller.currentMapNumber)
    // Lesson name has the map name in it (an animal), like "Ants 4". Just want the animal name
    return firstLessonOfMap?.name?.split(' ')[0] ?? '-'
  }

  get mapsAndPresenceForLinks() {
    const allMapNumbers = uniq(
      this.lessonInfo
        .lessonsFor(Precinct.RE_SPELLING)
        .map((l) => l.map)
        .filter(isPresent),
    )
    const mapNumbersHavingData = this.args.controller.mapNumbersWithLessonsCompleted
    const mapObjects = allMapNumbers.map((mapNumber) => {
      const hasData = mapNumbersHavingData?.includes(mapNumber)
      return { mapNumber, hasData: Boolean(hasData) }
    })
    return mapObjects
  }

  get mapsData() {
    const { mapNumbersWithLessonsCompleted } = this.args.controller
    const { completedLessonsData } = this
    const mapData = mapNumbersWithLessonsCompleted?.map((mapNumber) => {
      const lessons = this.lessonInfo
        .lessonsFor(Precinct.RE_SPELLING)
        .filter((lesson) => lesson.map === mapNumber)
        .map((lesson) => lesson.lesson_position)
        .filter(isPresent)
      const lessonData = lessons.map((lesson) => {
        return completedLessonsData?.find((map) => map.lesson_number === lesson) ?? this._buildblankLessonObject(lesson)
      })
      return { mapNumber, lessonData }
    })
    return mapData ?? []
  }

  get currentMapData() {
    const { currentMapNumber } = this.args.controller
    const { mapsData } = this
    const currentMapFound = mapsData.find((map) => map.mapNumber === currentMapNumber)
    let transposedMap = undefined
    if (currentMapFound) {
      const lessonData = currentMapFound.lessonData.map((lesson) => {
        const {
          attempts,
          challenge,
          created_at,
          focus_sound_words,
          high_frequency_sight_words,
          highest_score: highestScore,
          incorrect,
          latest_score: latestScore,
          lesson_number,
        } = lesson

        return {
          attempts: attempts,
          highest_score: highestScore,
          latest_score: latestScore,
          created_at: created_at ? unixTimeToCalendarIconArgs(created_at, this.intl) : undefined,
          challenge: challenge ? this._verifyAndJoinWords(challenge) : undefined,
          focus_sound_words: focus_sound_words ? this._verifyAndJoinWords(focus_sound_words) : undefined,
          high_frequency_sight_words: high_frequency_sight_words
            ? this._verifyAndJoinWords(high_frequency_sight_words)
            : undefined,
          incorrect: incorrect ? mapValues(incorrect, (ans) => truncate(ans, { length: 20 })) : undefined,
          lesson_number,
        }
      })

      // Changes an array of row objects of values to an object of key-named
      // arrays of values i.e.
      //  [
      //   { 'name': 'joker', 'age': 23 },
      //   { 'name': 'batman', 'age': 44 },
      //   { 'name': 'catwoman', 'age': 22, 'favecolour': 'black' },
      //  ]
      // becomes...
      //  {
      //   'name': ['joker', 'batman', 'catwoman'],
      //   'age': [23, 44, 22],
      //   'favecolour': ['black'],
      //  }
      transposedMap = transposeObjects(lessonData)
    }
    return transposedMap
  }

  get isEndOfMapTestCompleted() {
    return (this.endOfMapData ?? []).some((mapData) => mapData.map_number === this.args.controller.currentMapNumber)
  }

  get endOfMapDataFormatted() {
    const { isEndOfMapTestCompleted } = this
    if (isEndOfMapTestCompleted && this.endOfMapData) {
      const { currentMapNumber } = this.args.controller
      const mapCompleted = this.endOfMapData.find((map) => map.map_number === currentMapNumber)

      if (!mapCompleted) {
        return undefined
      }

      const {
        created_at,
        correct_count: correctCount,
        total_count: totalCount,
        correct: correctArray,
        incorrect: incorrectArray,
      } = mapCompleted

      const createdAt = unixTimeToCalendarIconArgs(created_at, this.intl)
      const correct = correctArray.join(', ')
      const incorrect = mapValues(incorrectArray, (ans) => truncate(ans, { length: 20 }))
      return {
        createdAt,
        correct,
        incorrect,
        correctCount,
        totalCount,
      }
    }
    return undefined
  }

  _buildblankLessonObject(lessonNumber: number) {
    return {
      attempts: null,
      challenge: null,
      created_at: null,
      focus_sound_words: null,
      high_frequency_sight_words: null,
      highest_score: null,
      incorrect: null,
      latest_score: null,
      lesson_number: lessonNumber,
    }
  }

  _verifyAndJoinWords(arrayOfWords: string[]) {
    return (arrayOfWords && arrayOfWords.join(', ')) || ''
  }

  <template>
    <ReportingTeacherUiHeader
      @title={{t "reporting.title.studentDetails.courseProgressSpelling" student=@model.student.fullName}}
      @showBack={{true}}
    />

    <ReportingDetailedControls
      @students={{@model.students}}
      @exportOptions={{array ExportType.print}}
      @isDataPresent={{Boolean this.completedLessonsData}}
      @studentRouteName="reporting.ui-scope.re.student-summary.course-progress.reading"
    />

    <ReportingNoDataMessageIf @on={{not this.completedLessonsData}}>
      <ReportingMapSelector
        @itemReferences={{this.mapsAndPresenceForLinks}}
        @currentItemId={{@controller.currentMapNumber}}
        @title="Map:"
        @changeItem={{@controller.changeMap}}
      />

      <div class="mt-8 space-y-14">
        <ContentTile data-test-course-progress-student-details-panel="lesson-details">
          <h3 data-test-student-details-header class="border-oceany-blue-150 border-b-4 p-4 print:border-black">
            {{t
              "reporting.re.studentSummary.courseProgress.spelling.studentDetails.levelHeader"
              currentMapNumber=@controller.currentMapNumber
              currentMapName=this.currentMapName
            }}

          </h3>
          <table class="w-full table-fixed" exportable>
            <thead>
              <tr class="bg-oceany-blue-100 border-b border-white">
                <th class="w-56 p-4 text-left font-bold">{{t
                    "reporting.re.studentSummary.courseProgress.spelling.studentDetails.lesson"
                  }}</th>
                {{#each this.currentMapData.lesson_number as |lesson|}}
                  <th class="p-4 text-center">{{String lesson}}</th>
                {{/each}}
              </tr>
            </thead>
            <tbody class="text-center">
              <tr class="hover:bg-oceany-blue-100 bg-white">
                <td class="w-56 p-4 text-left font-bold">{{t
                    "reporting.re.studentSummary.courseProgress.spelling.studentDetails.attempts"
                  }}</td>
                {{#each this.currentMapData.attempts as |attempt|}}
                  <td class="p-4 font-bold">{{formatValue attempt}}</td>
                {{/each}}
              </tr>
              <tr class="bg-oceany-blue-50 hover:bg-oceany-blue-100">
                <td class="w-56 p-4 text-left font-bold">{{t
                    "reporting.re.studentSummary.courseProgress.spelling.studentDetails.highestScore"
                  }}</td>
                {{#each this.currentMapData.highest_score as |highestScore|}}
                  <td class="p-4">{{formatValue highestScore append="%"}}</td>
                {{/each}}
              </tr>
            </tbody>
          </table>
        </ContentTile>
        <ContentTile data-test-course-progress-student-details-panel="progress-details">
          <h3 data-test-student-details-header class="border-oceany-blue-150 border-b-4 p-4 print:border-black">{{t
              "reporting.re.studentSummary.courseProgress.spelling.latestProgress.heading"
            }}</h3>
          <table class="w-full table-fixed" exportable>
            <tbody class="text-center">
              <tr class="hover:bg-oceany-blue-100 bg-white">
                <td class="w-56 p-4 text-left font-bold">{{t
                    "reporting.re.studentSummary.courseProgress.spelling.latestProgress.date"
                  }}</td>
                {{#each this.currentMapData.created_at as |date|}}
                  <td class="p-4">
                    {{#if date.value.month}}
                      {{#if date.value.day}}
                        <CalendarIcon
                          class="text-oceany-blue-300"
                          @month={{date.value.month}}
                          @day={{date.value.day}}
                        />
                      {{/if}}
                    {{/if}}
                    {{#unless (and date.value.month date.value.day)}}
                      {{t "reporting.re.studentSummary.courseProgress.spelling.latestProgress.noDate"}}
                    {{/unless}}
                  </td>
                {{/each}}
              </tr>
              <tr class="bg-oceany-blue-50 hover:bg-oceany-blue-100">
                <td class="w-56 p-4 text-left font-bold">{{t
                    "reporting.re.studentSummary.courseProgress.spelling.latestProgress.latestScore"
                  }}</td>
                {{#each this.currentMapData.latest_score as |latestScore|}}
                  <td>{{formatValue latestScore append="%"}}</td>
                {{/each}}
              </tr>
              <tr class="hover:bg-oceany-blue-100 bg-white">
                <td class="w-56 p-4 text-left font-bold">{{t
                    "reporting.re.studentSummary.courseProgress.spelling.latestProgress.focusSoundWords"
                  }}</td>
                {{#each this.currentMapData.focus_sound_words as |soundWords|}}
                  <td>{{formatValue soundWords}}</td>
                {{/each}}
              </tr>
              <tr class="bg-oceany-blue-50 hover:bg-oceany-blue-100">
                <td class="w-56 p-4 text-left font-bold">{{t
                    "reporting.re.studentSummary.courseProgress.spelling.latestProgress.highFrequencyWords"
                  }}</td>
                {{#each this.currentMapData.high_frequency_sight_words as |sightWords|}}
                  <td>{{formatValue sightWords}}</td>
                {{/each}}
              </tr>
              <tr class="hover:bg-oceany-blue-100 bg-white">
                <td class="w-56 p-4 text-left font-bold">{{t
                    "reporting.re.studentSummary.courseProgress.spelling.latestProgress.challenge"
                  }}</td>
                {{#each this.currentMapData.challenge as |challenge|}}
                  <td>{{formatValue challenge}}</td>
                {{/each}}
              </tr>
              <tr class="bg-oceany-blue-50 hover:bg-oceany-blue-100">
                <td class="w-56 p-4 text-left font-bold">{{t
                    "reporting.re.studentSummary.courseProgress.spelling.latestProgress.incorrect"
                  }}</td>
                {{#each this.currentMapData.incorrect as |incorrect|}}
                  <td>
                    {{#each-in incorrect as |correctWord incorrectSpelling|}}
                      <span
                        after=", "
                        class="whitespace-nowrap after:relative after:content-[attr(after)] last:after:hidden"
                      >{{correctWord}} ({{incorrectSpelling}})</span>
                    {{else}}
                      -
                    {{/each-in}}
                  </td>
                {{/each}}
              </tr>
            </tbody>
          </table>
        </ContentTile>

        {{#if this.isEndOfMapTestCompleted}}
          <ContentTile data-test-course-progress-student-details-panel="end-of-level-test">
            <h3 data-test-student-details-header class="border-oceany-blue-150 border-b-4 p-4 print:border-black">
              {{t "reporting.re.studentSummary.courseProgress.spelling.endOfLevel.heading"}}</h3>
            <table class="w-full table-fixed" exportable>
              <thead>
                <tr class="bg-oceany-blue-100 border-b border-white">
                  <th class="p-4">
                    {{t "reporting.re.studentSummary.courseProgress.spelling.endOfLevel.date"}}
                  </th>
                  <th data-test-correct-count class="p-4">
                    {{t
                      "reporting.re.studentSummary.courseProgress.spelling.endOfLevel.correct"
                      correctCount=this.endOfMapDataFormatted.correctCount
                      totalCount=this.endOfMapDataFormatted.totalCount
                    }}
                  </th>
                  <th class="p-4">
                    {{t "reporting.re.studentSummary.courseProgress.spelling.endOfLevel.incorrect"}}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr class="hover:bg-oceany-blue-100 bg-white">
                  <td class="p-4 text-left">
                    {{#let this.endOfMapDataFormatted.createdAt as |date|}}
                      {{#if date.value.month}}
                        {{#if date.value.day}}
                          <CalendarIcon
                            class="text-oceany-blue-300"
                            @month={{date.value.month}}
                            @day={{date.value.day}}
                          />
                        {{/if}}
                      {{/if}}
                      {{#unless (and date.value.month date.value.day)}}
                        {{t "reporting.re.studentSummary.courseProgress.spelling.endOfLevel.noDate"}}
                      {{/unless}}
                    {{/let}}
                  </td>
                  <td class="p-4 text-left">
                    {{this.endOfMapDataFormatted.correct}}
                  </td>
                  <td class="p-4 text-left">
                    {{#each-in this.endOfMapDataFormatted.incorrect as |correctWord incorrectSpelling|}}
                      <span
                        after=", "
                        class="whitespace-nowrap after:relative after:content-[attr(after)] last:after:hidden"
                      >{{correctWord}} ({{incorrectSpelling}})</span>
                    {{else}}
                      -
                    {{/each-in}}
                  </td>
                </tr>
              </tbody>
            </table>
          </ContentTile>
        {{/if}}
      </div>
    </ReportingNoDataMessageIf>
  </template>
}

export default RouteTemplate(ReStudentSummaryCourseProgressSpellingPage)
